import { useCallback, useEffect, useState } from 'react'
import { isNil, omitBy } from 'lodash'

import { useFetchRefactored, useRemoteCollection } from 'civic-champs-shared/api/hooks'
import { INTERNAL_ADMIN } from 'civic-champs-shared/auth/utils/permissions'
import { BulkableAction, CollectionEventListeners } from 'civic-champs-shared/api/hooks/useRemoteCollection'

import { useHasRole } from 'auth/hooks'
import useCreateOrUpdateOrganizationPrompt from './useCreateOrUpdateOrganizationPrompt'
import { Organization } from 'organization/types'

interface FetchOrganizationsParams {
  [key: string]: any
}

const useFetchOrganizations = () => {
  const [request, { loading, error }] = useFetchRefactored<Organization[]>()
  const isInternalAdmin = useHasRole(INTERNAL_ADMIN)
  const fetchOrganizations = useCallback(
    async (params?: FetchOrganizationsParams) =>
      request({
        url: `/organizations`,
        config: {
          queryStringParameters: { ...omitBy(params, isNil), reporting: isInternalAdmin },
        },
      }),
    [isInternalAdmin, request],
  )
  return { fetchOrganizations, loading, error }
}

const useOrganizationsCollection = (filters?: any) => {
  const [organizations, operations, events] = useRemoteCollection<Organization>()
  const [loading, setLoading] = useState(false)
  const [loadingProgress, setLoadingProgress] = useState(0)
  const [initiallyLoaded, setInitiallyLoaded] = useState(false)

  const { syncCollection, eagerAdd } = operations
  const { fetchOrganizations, error } = useFetchOrganizations()

  const getOrganizations = useCallback(
    async (filters?: any) => {
      setLoadingProgress(0)
      setLoading(true)
      const params: FetchOrganizationsParams = {}

      const updateOrganizations = await fetchOrganizations({ ...params })
      setLoadingProgress(100)
      setLoading(false)

      syncCollection(updateOrganizations)
      setInitiallyLoaded(true)
    },
    [fetchOrganizations, syncCollection],
  )

  const refresh = useCallback(() => getOrganizations(filters), [getOrganizations, filters])

  const addOrganizationLegacy = useCreateOrUpdateOrganizationPrompt(getOrganizations)

  useEffect(() => {
    refresh()
  }, [refresh])

  return [
    {
      organizations,
      loading,
      loadingProgress,
      error,
      initiallyLoaded,
    },
    {
      addOrganizationLegacy,
      refresh,
      eagerAdd,
    },
    events,
  ] as [
    {
      organizations: Organization[]
      loading: boolean
      loadingProgress: number
      error: Error | null
      initiallyLoaded: boolean
    },
    {
      addOrganizationLegacy: (organizationId?: number) => void
      refresh: () => Promise<void>
      eagerAdd: BulkableAction<Organization>
    },
    CollectionEventListeners<Organization>,
  ]
}

export default useOrganizationsCollection
